import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLandlordStore, StaffProfileDetail } from '@/stores/useLandlordStore';
import { ArrowLeft, Loader2, Shield, Settings, Wallet, Calendar, Check, Save } from 'lucide-react';
import { LoadingScreen } from '@/components/common/LoadingSpinner';
import { useToast } from '@/contexts/ToastContext';

const AVAILABLE_PERMISSIONS = [
    { id: 'view_financials', label: 'View Financials' },
    { id: 'manage_leases', label: 'Manage Leases' },
    { id: 'manage_maintenance', label: 'Manage Maintenance' },
    { id: 'communicate_tenants', label: 'Communicate with Tenants' }
];

export const StaffDetails: React.FC = () => {
    const { staffId } = useParams<{ staffId: string }>();
    const navigate = useNavigate();
    const { fetchStaffDetails, updateStaffPermissions, updateStaffPayroll } = useLandlordStore();
    const { success, error: toastError } = useToast();
    const [loading, setLoading] = useState(false);
    const [staffData, setStaffData] = useState<StaffProfileDetail | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState<'overview' | 'permissions' | 'payroll'>('overview');

    // Edit states
    const [permissions, setPermissions] = useState<string[]>([]);
    const [savingPermissions, setSavingPermissions] = useState(false);

    const [payrollSettings, setPayrollSettings] = useState({
        isAutomated: false,
        nextDueDate: '',
        paymentMethodId: ''
    });
    const [savingPayroll, setSavingPayroll] = useState(false);

    useEffect(() => {
        if (staffId) {
            loadStaffDetails(staffId);
        }
    }, [staffId]);

    const loadStaffDetails = async (id: string) => {
        setLoading(true);
        setError(null);
        try {
            const data = await fetchStaffDetails(id);
            setStaffData(data);
            setPermissions(data.permissions || []);
            setPayrollSettings({
                isAutomated: data.payrollSettings?.isAutomated || false,
                nextDueDate: data.payrollSettings?.nextDueDate || '',
                paymentMethodId: data.payrollSettings?.paymentMethodId || ''
            });
        } catch (err: any) {
            setError(err.message || 'Failed to load details.');
        } finally {
            setLoading(false);
        }
    };

    const handlePermissionToggle = (permissionId: string) => {
        setPermissions(prev =>
            prev.includes(permissionId)
                ? prev.filter(p => p !== permissionId)
                : [...prev, permissionId]
        );
    };

    const handleSavePermissions = async () => {
        if (!staffId) return;
        setSavingPermissions(true);
        try {
            await updateStaffPermissions(staffId, permissions);
            success('Permissions updated', 'Permissions updated successfully.');
            await loadStaffDetails(staffId);
        } catch (err: any) {
            toastError('Update failed', err.message);
        } finally {
            setSavingPermissions(false);
        }
    };

    const handleSavePayroll = async () => {
        if (!staffId) return;
        setSavingPayroll(true);
        try {
            await updateStaffPayroll(staffId, payrollSettings);
            success('Payroll updated', 'Payroll settings updated successfully.');
            await loadStaffDetails(staffId);
        } catch (err: any) {
            toastError('Update failed', err.message);
        } finally {
            setSavingPayroll(false);
        }
    };

    if (loading && !staffData) return <LoadingScreen />;

    if (error && !staffData) {
        return (
            <div className="p-6 text-center text-danger font-medium mt-10">
                <p>{error}</p>
                <button onClick={() => navigate('/landlord/staff')} className="mt-4 px-4 py-2 bg-light-card border rounded-lg hover:bg-light-bg">Go Back</button>
            </div>
        );
    }

    if (!staffData) return null;

    return (
        <div className="max-w-4xl mx-auto space-y-6 pb-24">
            <div className="flex items-center gap-4 bg-light-card dark:bg-dark-card p-4 rounded-xl shadow-sm border border-light-border dark:border-dark-border">
                <button
                    onClick={() => navigate('/landlord/staff')}
                    className="p-2 hover:bg-light-bg dark:hover:bg-dark-bg rounded-full transition-colors"
                >
                    <ArrowLeft size={20} className="text-light-subtle dark:text-dark-subtle" />
                </button>
                <div>
                    <h1 className="text-2xl font-bold text-light-text dark:text-dark-text flex items-center gap-2">
                        Staff Profile
                    </h1>
                </div>
            </div>

            <div className="bg-light-card dark:bg-dark-card rounded-2xl shadow-sm border border-light-border dark:border-dark-border overflow-hidden">
                {/* Header Profile Area */}
                <div className="bg-primary/5 p-8 border-b border-light-border dark:border-dark-border flex items-start justify-between relative overflow-hidden">
                    <div className="absolute top-0 left-0 w-full h-2 bg-primary" />

                    <div className="flex items-center gap-6">
                        <div className="h-20 w-20 rounded-full bg-gradient-to-tr from-primary to-primary/50 flex items-center justify-center text-white text-3xl font-bold shadow-md">
                            {staffData.profile.profilePic ? (
                                <img src={staffData.profile.profilePic} alt={staffData.profile.name} className="h-full w-full rounded-full object-cover" />
                            ) : (
                                staffData.profile.name.charAt(0).toUpperCase()
                            )}
                        </div>
                        <div>
                            <h2 className="text-3xl font-bold text-light-text dark:text-dark-text">
                                {staffData.profile.name}
                            </h2>
                            <div className="flex flex-wrap items-center gap-3 mt-2 text-sm text-light-subtle dark:text-dark-subtle font-medium">
                                <span className="flex items-center gap-1.5 px-3 py-1 bg-white dark:bg-dark-bg rounded-full shadow-sm">
                                    <Shield size={14} className="text-primary" />
                                    {staffData.assignment.role}
                                </span>
                                <span className="px-3 py-1 bg-white dark:bg-dark-bg rounded-full shadow-sm">
                                    Assigned to: {staffData.assignment.assignedTo}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Tabs Navigation */}
                <div className="flex px-6 border-b border-light-border dark:border-dark-border bg-light-card dark:bg-dark-card overflow-x-auto">
                    {[
                        { id: 'overview', label: 'Overview', icon: <Settings size={18} /> },
                        { id: 'permissions', label: 'Permissions', icon: <Shield size={18} /> },
                        { id: 'payroll', label: 'Payroll & Comp', icon: <Wallet size={18} /> }
                    ].map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id as any)}
                            className={`flex items-center gap-2 py-5 px-4 font-bold border-b-4 transition-colors whitespace-nowrap ${activeTab === tab.id
                                    ? 'border-primary text-primary'
                                    : 'border-transparent text-light-subtle dark:text-dark-subtle hover:text-light-text dark:hover:text-dark-text'
                                }`}
                        >
                            {tab.icon}
                            {tab.label}
                        </button>
                    ))}
                </div>

                {/* Tab Content */}
                <div className="p-8 pb-12 bg-light-bg dark:bg-dark-bg min-h-[400px]">

                    {/* OVERVIEW TAB */}
                    {activeTab === 'overview' && (
                        <div className="space-y-6 max-w-2xl">
                            <div>
                                <h3 className="text-sm font-bold text-light-subtle dark:text-dark-subtle uppercase tracking-wider mb-4">Contact Information</h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="bg-light-card dark:bg-dark-card p-5 rounded-xl border border-light-border dark:border-dark-border shadow-sm">
                                        <span className="text-xs text-light-subtle dark:text-dark-subtle font-bold uppercase tracking-wider block mb-1">Phone Number</span>
                                        <span className="text-lg font-bold text-light-text dark:text-dark-text">{staffData.profile.phone || 'Not provided'}</span>
                                    </div>
                                    <div className="bg-light-card dark:bg-dark-card p-5 rounded-xl border border-light-border dark:border-dark-border shadow-sm">
                                        <span className="text-xs text-light-subtle dark:text-dark-subtle font-bold uppercase tracking-wider block mb-2">Account Status</span>
                                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-bold ${staffData.assignment.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-700'
                                            }`}>
                                            {staffData.assignment.status}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* PERMISSIONS TAB */}
                    {activeTab === 'permissions' && (
                        <div className="space-y-6 max-w-4xl">
                            <div className="flex justify-between items-center bg-blue-50 dark:bg-blue-900/20 p-6 rounded-xl border border-blue-200 dark:border-blue-800">
                                <div>
                                    <h3 className="text-lg font-bold text-blue-900 dark:text-blue-100">Access Control Management</h3>
                                    <p className="text-sm font-medium text-blue-700 dark:text-blue-300 mt-1">Select the privileges granted to this staff member within the portal.</p>
                                </div>
                                <button
                                    onClick={handleSavePermissions}
                                    disabled={savingPermissions}
                                    className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl font-bold transition-all shadow-md disabled:opacity-50"
                                >
                                    {savingPermissions ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
                                    Save Permissions
                                </button>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {AVAILABLE_PERMISSIONS.map(p => (
                                    <label
                                        key={p.id}
                                        className={`flex items-center gap-4 p-5 rounded-xl border-2 cursor-pointer transition-all ${permissions.includes(p.id)
                                                ? 'border-primary bg-primary/5 shadow-sm'
                                                : 'border-light-border dark:border-dark-border hover:border-primary/50 bg-light-card dark:bg-dark-card'
                                            }`}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={permissions.includes(p.id)}
                                            onChange={() => handlePermissionToggle(p.id)}
                                            className="w-5 h-5 rounded text-primary focus:ring-primary"
                                        />
                                        <span className={`font-bold ${permissions.includes(p.id) ? 'text-primary' : 'text-light-text dark:text-dark-text'
                                            }`}>
                                            {p.label}
                                        </span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* PAYROLL TAB */}
                    {activeTab === 'payroll' && (
                        <div className="space-y-8 max-w-2xl">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="bg-light-card dark:bg-dark-card p-6 rounded-xl border border-light-border dark:border-dark-border shadow-sm flex flex-col justify-center">
                                    <span className="text-xs text-light-subtle dark:text-dark-subtle uppercase font-bold tracking-wider mb-2">Base Compensation</span>
                                    <span className="text-3xl font-bold text-light-text dark:text-dark-text">
                                        {staffData.compensation.amount ? `${staffData.compensation.currency || 'KES'} ${staffData.compensation.amount.toLocaleString()}` : 'Not Set'}
                                    </span>
                                    <span className="text-sm font-medium text-light-subtle dark:text-dark-subtle mt-1">per {staffData.compensation.frequency || 'period'}</span>
                                </div>
                            </div>

                            <div className="border border-light-border dark:border-dark-border bg-light-card dark:bg-dark-card rounded-2xl overflow-hidden shadow-sm">
                                <div className="bg-light-input dark:bg-dark-input p-6 border-b border-light-border dark:border-dark-border flex justify-between items-center">
                                    <div>
                                        <h3 className="font-bold text-lg text-light-text dark:text-dark-text">Payroll Automation</h3>
                                        <p className="text-sm font-medium text-light-subtle dark:text-dark-subtle mt-1">Should the system dispatch payouts automatically?</p>
                                    </div>
                                    <label className="relative inline-flex items-center cursor-pointer">
                                        <input
                                            type="checkbox"
                                            className="sr-only peer"
                                            checked={payrollSettings.isAutomated}
                                            onChange={(e) => setPayrollSettings({ ...payrollSettings, isAutomated: e.target.checked })}
                                        />
                                        <div className="w-14 h-7 bg-light-border dark:bg-dark-border peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-primary shadow-inner"></div>
                                    </label>
                                </div>

                                <div className={`p-6 space-y-6 transition-all ${payrollSettings.isAutomated ? 'opacity-100' : 'opacity-40 pointer-events-none'}`}>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                        <div>
                                            <label className="block text-sm font-bold text-light-text dark:text-dark-text mb-2 flex items-center gap-2">
                                                <Calendar size={18} className="text-light-subtle dark:text-dark-subtle" /> 
                                                Next Due Date
                                            </label>
                                            <input
                                                type="date"
                                                value={payrollSettings.nextDueDate}
                                                onChange={(e) => setPayrollSettings({ ...payrollSettings, nextDueDate: e.target.value })}
                                                className="w-full px-4 py-3 rounded-xl border border-light-border dark:border-dark-border bg-light-input dark:bg-dark-input text-light-text dark:text-dark-text focus:ring-2 focus:ring-primary/50 outline-none font-medium"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-bold text-light-text dark:text-dark-text mb-2 flex items-center gap-2">
                                                <Wallet size={18} className="text-light-subtle dark:text-dark-subtle" /> 
                                                Payment Method ID
                                            </label>
                                            <input
                                                type="text"
                                                placeholder="e.g. mpesa-123"
                                                value={payrollSettings.paymentMethodId}
                                                onChange={(e) => setPayrollSettings({ ...payrollSettings, paymentMethodId: e.target.value })}
                                                className="w-full px-4 py-3 rounded-xl border border-light-border dark:border-dark-border bg-light-input dark:bg-dark-input text-light-text dark:text-dark-text focus:ring-2 focus:ring-primary/50 outline-none font-medium"
                                            />
                                        </div>
                                    </div>

                                    <div className="flex justify-end pt-4 border-t border-light-border dark:border-dark-border mt-6">
                                        <button
                                            onClick={handleSavePayroll}
                                            disabled={savingPayroll}
                                            className="flex items-center gap-2 bg-primary text-white px-8 py-3 rounded-xl font-bold shadow-md transition-all hover:bg-primary/90 disabled:opacity-50"
                                        >
                                            {savingPayroll ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
                                            Save Payroll Settings
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
